import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Share2, RotateCcw } from "lucide-react";
import TopNav from "../components/landing/TopNav";
import Footer from "../components/landing/Footer";
import {
    fetchArticles,
    fetchArticleContent,
    readingTime,
} from "../lib/soro";

const REVEAL = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};

export default function BlogPost() {
    const { slug } = useParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const reduce = useReducedMotion();

    const [state, setState] = useState({
        status: "loading", // loading | ok | not_found | error
        article: null,
        content: "",
        prev: null,
        next: null,
    });
    const [shareToast, setShareToast] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setState({
                status: "loading",
                article: null,
                content: "",
                prev: null,
                next: null,
            });
            try {
                const list = await fetchArticles();
                if (cancelled) return;
                if (!Array.isArray(list) || list.length === 0) {
                    setState({
                        status: "not_found",
                        article: null,
                        content: "",
                        prev: null,
                        next: null,
                    });
                    return;
                }
                const idx = list.findIndex((a) => a.slug === slug);
                if (idx === -1) {
                    setState({
                        status: "not_found",
                        article: null,
                        content: "",
                        prev: null,
                        next: null,
                    });
                    return;
                }
                const article = list[idx];
                const prev = idx > 0 ? list[idx - 1] : null;
                const next = idx < list.length - 1 ? list[idx + 1] : null;
                try {
                    const html = await fetchArticleContent(article);
                    if (cancelled) return;
                    setState({
                        status: "ok",
                        article,
                        content: html || "",
                        prev,
                        next,
                    });
                } catch (_err) {
                    if (cancelled) return;
                    setState({
                        status: "error",
                        article,
                        content: "",
                        prev,
                        next,
                    });
                }
            } catch (_err) {
                if (cancelled) return;
                setState({
                    status: "error",
                    article: null,
                    content: "",
                    prev: null,
                    next: null,
                });
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    // Update document title + meta description for SEO and share previews.
    useEffect(() => {
        const a = state.article;
        if (a && state.status === "ok") {
            document.title = `${a.title} — BetweenUs`;
            const descMeta = document.querySelector('meta[name="description"]');
            if (descMeta && a.excerpt) {
                descMeta.setAttribute("content", a.excerpt);
            }
        } else if (state.status === "not_found") {
            document.title = "Note not found — BetweenUs";
        }
    }, [state.article, state.status]);

    const anim = reduce ? {} : REVEAL;
    const article = state.article;
    const mins = state.content ? readingTime(state.content) : null;

    const onShare = async () => {
        const url = window.location.href;
        const title = article?.title || "BetweenUs";
        try {
            if (navigator.share) {
                await navigator.share({ title, url });
                return;
            }
            await navigator.clipboard.writeText(url);
            setShareToast("Link copied");
            setTimeout(() => setShareToast(""), 1800);
        } catch (_e) {
            // ignore
        }
    };

    return (
        <div
            className="relative min-h-screen bg-paper text-ink flex flex-col"
            data-testid="blog-post-page"
        >
            <TopNav variant="legal" />

            <main className="flex-1">
                {/* Hero header */}
                <section
                    data-testid="blog-post-hero"
                    className="relative overflow-hidden border-b border-brand"
                >
                    <div className="absolute inset-0 hero-wash pointer-events-none" />
                    <div className="noise-overlay" />
                    <div className="relative mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10 sm:pb-12">
                        <Link
                            to="/blog"
                            data-testid="blog-post-back"
                            className="inline-flex items-center gap-2 text-[12.5px] font-medium tracking-[-0.01em] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                        >
                            <ArrowLeft size={14} strokeWidth={2} />
                            All notes
                        </Link>

                        {state.status === "loading" && (
                            <div
                                data-testid="blog-post-loading"
                                aria-hidden="true"
                                className="mt-6"
                            >
                                <div className="h-3 w-32 rounded-full bg-[rgba(14,27,42,0.06)]" />
                                <div className="mt-5 h-9 w-3/4 rounded-md bg-[rgba(14,27,42,0.08)]" />
                                <div className="mt-3 h-9 w-2/3 rounded-md bg-[rgba(14,27,42,0.08)]" />
                                <div className="mt-6 h-4 w-1/2 rounded-md bg-[rgba(14,27,42,0.05)]" />
                            </div>
                        )}

                        {state.status === "not_found" && (
                            <NotFoundBlock onBack={() => navigate("/blog")} />
                        )}

                        {state.status === "error" && (
                            <ErrorBlock onRetry={() => window.location.reload()} />
                        )}

                        {state.status === "ok" && article && (
                            <motion.div
                                {...anim}
                                className="mt-6"
                                data-testid="blog-post-header"
                            >
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--brand-muted)]">
                                    {article.date ? (
                                        <time dateTime={article.isoDate || article.date}>
                                            {article.date}
                                        </time>
                                    ) : null}
                                    {mins ? (
                                        <>
                                            <span
                                                aria-hidden="true"
                                                className="opacity-50"
                                            >
                                                ·
                                            </span>
                                            <span className="inline-flex items-center gap-1.5">
                                                <Clock
                                                    size={11}
                                                    strokeWidth={2}
                                                />
                                                {mins}
                                            </span>
                                        </>
                                    ) : null}
                                </div>
                                <h1
                                    data-testid="blog-post-title"
                                    className="mt-3 font-serif text-[34px] sm:text-[44px] leading-[1.08] tracking-[-0.02em] text-[var(--brand-ink)]"
                                >
                                    {article.title}
                                </h1>
                                {article.excerpt ? (
                                    <p className="mt-4 text-[16.5px] leading-[1.65] text-[var(--brand-muted-2)] max-w-2xl">
                                        {article.excerpt}
                                    </p>
                                ) : null}
                                <div className="mt-6 flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={onShare}
                                        data-testid="blog-post-share"
                                        className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-brand bg-white text-[12.5px] font-semibold tracking-[-0.01em] text-[var(--brand-ink)] hover:bg-[var(--brand-paper-2)] transition-colors ring-focus"
                                    >
                                        <Share2 size={13} strokeWidth={2} />
                                        Share
                                    </button>
                                    {shareToast ? (
                                        <span
                                            role="status"
                                            className="text-[12.5px] font-medium text-[var(--brand-teal-2)]"
                                        >
                                            {shareToast}
                                        </span>
                                    ) : null}
                                </div>
                            </motion.div>
                        )}
                    </div>
                </section>

                {/* Article body */}
                {state.status === "ok" && article && (
                    <section className="relative">
                        <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                            {article.image ? (
                                <figure className="mb-8 sm:mb-10 overflow-hidden rounded-[20px] border border-brand shadow-[var(--shadow-soft)] bg-[var(--brand-paper-2)]">
                                    <img
                                        src={article.image}
                                        alt={article.title || ""}
                                        loading="eager"
                                        className="block w-full h-auto"
                                    />
                                </figure>
                            ) : null}

                            <article
                                data-testid="blog-post-body"
                                className="blog-prose"
                                // TrySoro authors content — we trust their HTML
                                // and render natively. All anchors are routed
                                // through processBodyLinks after mount.
                                dangerouslySetInnerHTML={{
                                    __html: state.content || "",
                                }}
                                ref={processBodyLinks}
                            />

                            {/* Prev / Next nav */}
                            {(state.prev || state.next) && (
                                <nav
                                    data-testid="blog-post-pager"
                                    aria-label="More notes"
                                    className="mt-12 sm:mt-14 grid gap-4 sm:grid-cols-2"
                                >
                                    {state.prev ? (
                                        <PagerLink
                                            article={state.prev}
                                            label="Previous note"
                                            align="left"
                                            testId="blog-post-prev"
                                        />
                                    ) : (
                                        <span aria-hidden="true" />
                                    )}
                                    {state.next ? (
                                        <PagerLink
                                            article={state.next}
                                            label="Next note"
                                            align="right"
                                            testId="blog-post-next"
                                        />
                                    ) : (
                                        <span aria-hidden="true" />
                                    )}
                                </nav>
                            )}

                            <div className="mt-10 sm:mt-14 flex flex-wrap items-center justify-between gap-4 text-[12.5px]">
                                <Link
                                    to="/blog"
                                    className="inline-flex items-center gap-2 text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                                >
                                    <ArrowLeft size={13} strokeWidth={2} />
                                    Back to all notes
                                </Link>
                                <Link
                                    to="/support"
                                    className="underline decoration-[var(--brand-teal-2)] underline-offset-2 text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)]"
                                >
                                    Have a topic? Send us a note
                                </Link>
                            </div>
                        </div>
                    </section>
                )}
            </main>

            <Footer />
        </div>
    );
}

function processBodyLinks(node) {
    if (!node) return;
    // Ensure all external links from TrySoro content open safely.
    try {
        const links = node.querySelectorAll("a");
        for (const a of links) {
            const href = a.getAttribute("href") || "";
            if (/^https?:\/\//i.test(href)) {
                a.setAttribute("target", "_blank");
                a.setAttribute("rel", "noopener noreferrer");
            }
        }
        // Ensure images get lazy loaded.
        const imgs = node.querySelectorAll("img");
        for (const img of imgs) {
            if (!img.getAttribute("loading")) {
                img.setAttribute("loading", "lazy");
            }
            img.setAttribute("decoding", "async");
        }
    } catch (_e) {
        // best-effort
    }
}

function PagerLink({ article, label, align = "left", testId }) {
    const Icon = align === "left" ? ArrowLeft : ArrowRight;
    return (
        <Link
            to={`/blog/${article.slug}`}
            data-testid={testId}
            className={`group flex flex-col rounded-[16px] border border-brand bg-white p-4 sm:p-5 shadow-[var(--shadow-soft)] hover:-translate-y-[1px] hover:border-[rgba(48,140,138,0.45)] transition-[transform,border-color,box-shadow] duration-200 ring-focus ${
                align === "right" ? "sm:items-end sm:text-right" : ""
            }`}
        >
            <span
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--brand-teal-2)] ${
                    align === "right" ? "flex-row-reverse" : ""
                }`}
            >
                <Icon size={12} strokeWidth={2.2} />
                {label}
            </span>
            <span className="mt-2 font-serif text-[16.5px] leading-[1.3] tracking-[-0.01em] text-[var(--brand-ink)] group-hover:text-[var(--brand-navy)] transition-colors">
                {article.title}
            </span>
        </Link>
    );
}

function NotFoundBlock({ onBack }) {
    return (
        <div className="mt-6" data-testid="blog-post-not-found">
            <h1 className="font-serif text-[32px] sm:text-[40px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]">
                We can’t find that note.
            </h1>
            <p className="mt-3 text-[15.5px] leading-[1.6] text-[var(--brand-muted-2)] max-w-xl">
                The link may have changed, or the note hasn’t been
                published yet. Head back to the index to see what’s new.
            </p>
            <button
                type="button"
                onClick={onBack}
                className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[13.5px] font-semibold tracking-[-0.01em] hover:bg-[var(--brand-navy-2)] transition-colors shadow-[0_10px_24px_rgba(14,27,42,0.14)] ring-focus"
            >
                <ArrowLeft size={14} strokeWidth={2} />
                Browse all notes
            </button>
        </div>
    );
}

function ErrorBlock({ onRetry }) {
    return (
        <div className="mt-6" data-testid="blog-post-error">
            <h1 className="font-serif text-[32px] sm:text-[40px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]">
                Something went wrong loading this note.
            </h1>
            <p className="mt-3 text-[15.5px] leading-[1.6] text-[var(--brand-muted-2)] max-w-xl">
                Please check your connection and try again. If the issue
                continues, drop us a line at support@tylegn.com.
            </p>
            <button
                type="button"
                onClick={onRetry}
                className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[13.5px] font-semibold tracking-[-0.01em] hover:bg-[var(--brand-navy-2)] transition-colors shadow-[0_10px_24px_rgba(14,27,42,0.14)] ring-focus"
            >
                <RotateCcw size={14} strokeWidth={2} />
                Try again
            </button>
        </div>
    );
}
